import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../api';
import { formatOrdinalDay, formatPhoneNumber } from '../utils/formatters';
import { useConfig } from '../context/ConfigContext';
import { describeLateFee } from '../utils/lateFeeDisplay';
// "Monthly Payment" means P&I PLUS the escrow frozen on the loan. loan.monthly_payment
// is P&I alone and must never be shown under that label. See utils/monthlyPayment.js.
import { totalMonthlyPayment, monthlyPaymentSplitText, amountDue, arrearsBalance } from '../utils/monthlyPayment';
import RefundModal from '../components/RefundModal';
import TaxInstallmentLines from '../components/TaxInstallmentLines';

// ---------------------------------------------------------------------------
// Active / overdue / late fee — NOT defined here.
//
// The server decides all three and sends them on every loan row from
// GET /api/admin/loans (is_active, is_overdue, days_past_due, late_fee_applies),
// computed from the shared SQL in server.js. This page only reads them. That is
// what keeps the stat cards, the tab counts and the row badges on this page in
// agreement with each other AND with the dashboard — they are one definition,
// not several that happen to match.
//
// The rule those flags implement:
//   ACTIVE  = not paid off, defaulted or archived.
//   OVERDUE = due date is in the past. A payment due TODAY is NOT overdue.
//   ACTIVE and OVERDUE OVERLAP — a loan one day late is BOTH.
//   The grace period changes the late FEE only, never the overdue flag.
//
// Do not reintroduce a local copy of these. A second definition is the bug this
// file already had.
// ---------------------------------------------------------------------------

const isActive = (loan) => loan?.is_active === true;
const isOverdue = (loan) => loan?.is_overdue === true;
const getDaysOverdue = (loan) => Number(loan?.days_past_due) || 0;

// Past the grace window, waiver or not — when the waive control is offered.
const isPastGracePeriod = (loan) => loan?.past_grace_period === true;
const isLateFeeWaived = (loan) => loan?.late_fee_waived === true;

// Past due but still inside the grace window: no late fee yet. Presentation
// only — these loans are fully OVERDUE and counted as such.
const isWithinGrace = (loan) => isOverdue(loan) && loan?.late_fee_applies !== true;

// The two visually distinct overdue states. BOTH are overdue and both are
// counted in the Overdue tally; only the styling and the wording differ, because
// "past due, no fee yet" and "past due, fee charged" need different responses.
//   amber = within grace, late fee NOT in the cure amount
//   red   = grace expired, late fee included
const overdueTone = (loan) => {
  const days = getDaysOverdue(loan);
  const dayWord = days === 1 ? 'day' : 'days';
  return isWithinGrace(loan)
    ? {
        accent: '#b45309',      // amber-700: readable on white, unlike raw #ffc107
        border: 'var(--color-warning-border)',
        surface: '#fffbeb',
        badgeBg: '#f59e0b',
        badgeText: '#3f2d00',
        label: `${days} ${dayWord} late`,
        note: 'within grace period'
      }
    : {
        accent: '#dc3545',
        border: 'var(--color-error-border)',
        surface: '#fff5f5',
        badgeBg: '#dc3545',
        badgeText: '#ffffff',
        label: `${days} ${dayWord} late`,
        note: null
      };
};

function AdminLoans() {
  const navigate = useNavigate();
  // No grace period read here on purpose: the server applies the loan's own
  // grace (frozen at origination) when it computes late_fee_applies, so this
  // page must not second-guess it with the tenant's *current* setting.
  //
  // getLateFee() is the tenant's DEFAULT fee amount only, used when a loan
  // carries no late_fee_amount of its own -- the same `||` fallback the server
  // uses. It never decides WHETHER a fee applies.
  //
  // getNoticeFee/getCurePeriodDays drive the default-notice preview. Those are
  // TENANT settings, not loan-frozen values: the notice fee is levied now, and
  // the cure window is the one this tenant grants today. Contrast getLateFee
  // above, which only ever fills in for a loan that carries no fee of its own.
  const { isDemoMode, getLateFee, getNoticeFee, getCurePeriodDays, getNoticeEligibilityDays, getPaymentDueDayMode, getPaymentDueDayOptions } = useConfig();
  // The two Due Day selects below offered a hardcoded 1st and 15th, so an admin
  // could not set a loan to any other day -- and for a tenant offering the 10th
  // and the 20th, BOTH options were rejected by the PATCH route that validates
  // against the tenant's own list.
  //
  // In anniversary mode there is no offered list at all, so the admin control
  // opens up to every real day of the month. That mirrors adminSelectableDueDays()
  // in server.js, which is what the PATCH route validates against: this mode
  // governs how a NEW loan is originated, and an admin must still be able to
  // correct an existing one.
  const adminDueDays = getPaymentDueDayMode() === 'anniversary'
    ? Array.from({ length: 31 }, (_, i) => i + 1)
    : getPaymentDueDayOptions();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [defaultingLoan, setDefaultingLoan] = useState(null);
  const [defaultFormData, setDefaultFormData] = useState({
    default_date: new Date().toISOString().split('T')[0],
    recovery_costs: '',
    default_notes: ''
  });
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [noticeForm, setNoticeForm] = useState({
    notice_date: new Date().toISOString().split('T')[0],
    postal_method: 'Certified Mail with Return Receipt',
    postal_cost: '',
    tracking_number: '',
    notes: ''
  });
  // The cure window THIS notice will actually grant. The server computes
  // cure_deadline_date from the value frozen on the loan at origination -- what
  // that loan's own signed contract states -- so the preview has to read the
  // same field or it will show the admin a date the server does not write.
  // Falls back to the tenant's current setting only for a loan with no frozen
  // value, which is also the fallback the server uses.
  const noticeCurePeriodDays = selectedLoan?.cure_period_days ?? getCurePeriodDays();

  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentLoan, setManualPaymentLoan] = useState(null);

  // Recording a REMITTANCE: money leaving the escrow account for the county or
  // the association. The opposite direction to Record Payment above, which is
  // money arriving from the buyer, and a separate ledger from the loan balance.
  const [showEscrowPaymentModal, setShowEscrowPaymentModal] = useState(false);
  const [escrowPaymentLoan, setEscrowPaymentLoan] = useState(null);
  const [escrowPaymentData, setEscrowPaymentData] = useState({
    kind: 'tax',
    amount: '',
    paid_date: new Date().toISOString().split('T')[0],
    tax_year: new Date().getFullYear(),
    instalment: '',
    payment_method: 'Check',
    reference: '',
    notes: ''
  });
  // The county schedule for the loan being remitted against, and what has been
  // paid on it. Supplied by the server, which decides every status on it.
  const [escrowTaxSchedule, setEscrowTaxSchedule] = useState(null);

  // Re-pricing the frozen escrow. Contract Section 8(b) allows one change per
  // twelve months, which the SERVER enforces; this form collects the reason and
  // the effective date and reports the refusal when it comes back.
  const [showAdjustEscrowModal, setShowAdjustEscrowModal] = useState(false);
  const [adjustEscrowLoan, setAdjustEscrowLoan] = useState(null);
  const [adjustEscrowData, setAdjustEscrowData] = useState({
    annual_tax_amount: '',
    monthly_hoa_fee: '',
    effective_date: '',
    reason: ''
  });
  const [adjustEscrowError, setAdjustEscrowError] = useState('');
  const [adjustEscrowSaving, setAdjustEscrowSaving] = useState(false);
  const [manualPaymentData, setManualPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    transaction_id: '',
    notes: '',
    // Never default to charging. openManualPaymentModal() sets this from
    // describeLateFee().checked once a loan is chosen; until then the safe
    // value is "do not charge a fee", not "charge one".
    includeLateFee: false,
    // Recording a payment BELOW the amount due carries the difference as
    // arrears, so it has to be a decision rather than a slip of the keyboard.
    // The submit handler refuses while the preview says partial and this is
    // false; openManualPaymentModal() resets it for every loan.
    recordAsPartial: false
  });
  const [paymentPreview, setPaymentPreview] = useState(null);  // NEW: Preview breakdown
  const [loadingPreview, setLoadingPreview] = useState(false);  // NEW: Loading state
  // Refund flow: pick a payment from the loan's history, then confirm.
  const [showRefundPicker, setShowRefundPicker] = useState(false);
  const [refundLoan, setRefundLoan] = useState(null);
  const [refundPayments, setRefundPayments] = useState([]);
  const [loadingRefundPayments, setLoadingRefundPayments] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundPreview, setRefundPreview] = useState(null);
  const [showDeedModal, setShowDeedModal] = useState(false);
  const [deedModalLoan, setDeedModalLoan] = useState(null);
  const [deedFormData, setDeedFormData] = useState({
    deed_type: 'Special Warranty Deed',
    deed_name: '',
    deed_mailing_address: ''
  });

  useEffect(() => {
    loadLoans();
  }, []);

  useEffect(() => {
    filterLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loans, filter, searchTerm]);

  const loadLoans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch loans');
      }

      const data = await response.json();
      setLoans(data);
    } catch (err) {
      setError('Failed to load loans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadContract = async (loanId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loanId}/contract`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (err) {
      console.error('Failed to load contract:', err);
      return null;
    }
  };

  const filterLoans = useCallback(() => {
    let filtered = [...loans];

    // Apply status filter. ACTIVE includes overdue loans — overdue is a
    // condition layered on active, not a replacement status — so the Active tab
    // is a superset of the Overdue tab and the two counts overlap by design.
    if (filter === 'all') {
      filtered = filtered.filter(loan => loan.status !== 'archived');
    } else if (filter === 'active') {
      filtered = filtered.filter(loan => isActive(loan));
    } else if (filter === 'overdue') {
      filtered = filtered.filter(loan => isOverdue(loan));
    } else if (filter === 'paid_off') {
      filtered = filtered.filter(loan => loan.status === 'paid_off');
    } else if (filter === 'defaulted') {
      filtered = filtered.filter(loan => loan.status === 'defaulted');
    } else if (filter === 'archived') {
      filtered = filtered.filter(loan => loan.status === 'archived');
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.first_name?.toLowerCase().includes(search) ||
        loan.last_name?.toLowerCase().includes(search) ||
        loan.email?.toLowerCase().includes(search) ||
        loan.property_title?.toLowerCase().includes(search)
      );
    }

    setFilteredLoans(filtered);
  }, [loans, filter, searchTerm]);

  // What the manual payment modal shows for the late fee: visible, enabled,
  // checked, and the wording. Derived entirely from the server's flags on the
  // loan -- see client/src/utils/lateFeeDisplay.js.
  const lateFeeView = describeLateFee(manualPaymentLoan, {
    defaultLateFeeAmount: getLateFee()
  });

  const toggleAlert = async (loanId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'enable' : 'disable'} alerts for this loan?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loanId}/toggle-alert`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle alert');
      }

      // Reload loans
      loadLoans();
    } catch (err) {
      alert('Failed to update alert status');
      console.error(err);
    }
  };

  const openDefaultModal = (loan) => {
    setDefaultingLoan(loan);
    setDefaultFormData({
      default_date: new Date().toISOString().split('T')[0],
      recovery_costs: '',
      default_notes: ''
    });
    setShowDefaultModal(true);
  };

  const handleDefaultSubmit = async (e) => {
    e.preventDefault();

    if (!window.confirm(`Mark "${defaultingLoan.property_title}" as DEFAULTED?\n\nThis will:\n- Set loan status to Defaulted\n- Set property back to Available\n- Calculate net recovery\n\nContinue?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${defaultingLoan.id}/default`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(defaultFormData)
      });

      if (!response.ok) {
        throw new Error('Failed to mark as defaulted');
      }

      const result = await response.json();
      alert(`Loan marked as defaulted. Net recovery: $${result.netRecovery.toFixed(2)}`);
      setShowDefaultModal(false);
      setDefaultingLoan(null);
      loadLoans();
    } catch (err) {
      alert('Failed to mark loan as defaulted');
      console.error(err);
    }
  };

  const openNoticeModal = (loan) => {
    setSelectedLoan(loan);
    setShowNoticeModal(true);
    setNoticeForm({
      notice_date: new Date().toISOString().split('T')[0],
      postal_method: 'Certified Mail with Return Receipt',
      postal_cost: '',
      tracking_number: '',
      notes: ''
    });
  };

  const handleSendNotice = async (e) => {
    e.preventDefault();
    
    if (!noticeForm.postal_cost || !noticeForm.tracking_number) {
      alert('Postal cost and tracking number are required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${selectedLoan.id}/send-notice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(noticeForm)
      });
      
      if (!response.ok) throw new Error('Failed');
      
      alert('Default/Cure Notice recorded successfully!');
      setShowNoticeModal(false);
      loadLoans();
    } catch (err) {
      console.error('Failed to send notice:', err);
      alert('Failed to record notice');
    }
  };

  const waiveLateFee = async (loanId) => {
    if (!window.confirm('Waive late fee for this loan?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loanId}/waive-late-fee`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      // Parse the body first - the server reports the real reason on failure,
      // and the confirmed new cure amount on success.
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        data = null;
      }

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || `Waive failed (HTTP ${response.status})`);
      }

      // Refresh before confirming, so the number on screen matches the message.
      await loadLoans();

      const newCure = typeof data.cureAmount === 'number'
        ? `$${data.cureAmount.toFixed(2)}`
        : 'updated';
      alert(`Late fee waived. New cure amount: ${newCure}\n\nReminder: Inform customer on next call.`);
    } catch (err) {
      console.error('Waive late fee failed:', err);
      alert(`Failed to waive late fee: ${err.message}`);
    }
  };

  // ---- ESCROW REMITTANCES -------------------------------------------------
  // Money going OUT to the county or the association, recorded against the
  // loan that collected it. Reachable from the loan row because that is where
  // the collection is visible; the property form reaches the same endpoint.
  const loadEscrowTaxSchedule = async (loanId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/loans/${loanId}/tax-schedule`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      if (!response.ok) return;
      setEscrowTaxSchedule(await response.json());
    } catch {
      // A schedule that cannot be loaded costs the admin a prefill, not the
      // ability to record a remittance. The form stays fully usable.
    }
  };

  // Prefill from the property's own schedule, and ONLY when the dropdown
  // changes. The amount stays editable afterwards: nothing here runs again to
  // undo what the admin typed, which is why this is a handler and not an effect.
  const applyInstalmentSelection = (value) => {
    setEscrowPaymentData((prev) => {
      const next = { ...prev, instalment: value };
      if (!escrowTaxSchedule) return next;

      if (value === '') {
        if (escrowTaxSchedule.annual_tax_amount > 0) {
          next.amount = escrowTaxSchedule.annual_tax_amount.toFixed(2);
        }
        return next;
      }

      const chosen = (escrowTaxSchedule.instalments || [])
        .find((row) => String(row.instalment) === String(value));
      if (!chosen) return next;

      if (chosen.amount > 0) next.amount = chosen.amount.toFixed(2);
      // The tax year follows the selected installment's OWN due date, so a 2nd
      // installment due in September 2026 defaults to 2026 and a 1st
      // installment due in February 2027 defaults to 2027, rather than to
      // whatever calendar year the admin happens to be sitting in.
      if (chosen.tax_year) next.tax_year = chosen.tax_year;
      return next;
    });
  };

  const openEscrowPaymentModal = (loan) => {
    setEscrowPaymentLoan(loan);
    setEscrowPaymentData({
      kind: 'tax',
      amount: '',
      paid_date: new Date().toISOString().split('T')[0],
      tax_year: new Date().getFullYear(),
      instalment: '',
      payment_method: 'Check',
      reference: '',
      notes: ''
    });
    setEscrowTaxSchedule(null);
    setShowEscrowPaymentModal(true);
    loadEscrowTaxSchedule(loan.id);
  };

  const submitEscrowPayment = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/escrow-disbursements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          ...escrowPaymentData,
          loan_id: escrowPaymentLoan.id,
          property_id: escrowPaymentLoan.property_id,
          // Blank means "not tied to one instalment", which the server stores as
          // null and the settlement rule reads as covering the whole year.
          instalment: escrowPaymentData.instalment === '' ? null : escrowPaymentData.instalment
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to record remittance');
      }
      setShowEscrowPaymentModal(false);
      alert('Escrow remittance recorded.');
      loadLoans();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---- RE-PRICING THE FROZEN ESCROW ---------------------------------------
  const openAdjustEscrowModal = (loan) => {
    setAdjustEscrowLoan(loan);
    setAdjustEscrowData({
      annual_tax_amount: '',
      monthly_hoa_fee: '',
      effective_date: '',
      reason: ''
    });
    setAdjustEscrowError('');
    setShowAdjustEscrowModal(true);
  };

  const submitAdjustEscrow = async (e) => {
    e.preventDefault();
    setAdjustEscrowError('');
    setAdjustEscrowSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/loans/${adjustEscrowLoan.id}/adjust-escrow`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify(adjustEscrowData)
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        // The twelve month covenant is enforced on the SERVER. Surface its own
        // message rather than second-guessing it here, so the rule has one
        // statement and the admin is told the date they may next adjust.
        setAdjustEscrowError(body.error || 'Failed to adjust escrow');
        return;
      }
      setShowAdjustEscrowModal(false);
      alert(
        'Escrow updated.\n\n' +
        (body.noticeReminder ||
          'Written notice of this change is required before it takes effect.')
      );
      loadLoans();
    } catch (err) {
      setAdjustEscrowError('Failed to adjust escrow');
    } finally {
      setAdjustEscrowSaving(false);
    }
  };

  const openManualPaymentModal = (loan) => {
    setManualPaymentLoan(loan);
    // The TOTAL this period owes -- P&I plus the loan's frozen escrow -- not
    // loans.monthly_payment, which is P&I alone. The modal was prefilling 97.50
    // on a loan that owes 107.08, and the manual path validates the amount
    // against nothing, so an admin accepting the prefill recorded a payment
    // 9.58 short and the loan's due date advanced anyway.
    // ...and now plus any arrears the loan carries, which is what squares the
    // buyer with the schedule rather than merely settling this period. A
    // prefill of the bare monthly payment on a loan already behind is how the
    // shortfall stayed invisible: the admin accepted the figure, the due date
    // advanced, and the deficiency rolled on unnamed.
    const scheduledTotal = amountDue(loan);
    const initialAmount = scheduledTotal > 0 ? scheduledTotal.toFixed(2) : '';
    // Ticked only when a fee is genuinely due -- same call the checkbox itself
    // renders from, so the default state and the label can never disagree.
    const shouldApplyLateFee = describeLateFee(loan, {
      defaultLateFeeAmount: getLateFee()
    }).checked;

    setManualPaymentData({
      amount: initialAmount,
      // Reset for every loan. A tick carried over from the last customer would
      // let a short payment through without anyone confirming it.
      recordAsPartial: false,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      transaction_id: '',
      notes: '',
      includeLateFee: shouldApplyLateFee,  // Checked only if overdue
      // Extra principal paid on its own, between due dates. Only available when
      // nothing is due -- if a payment IS due the waterfall applies the excess
      // to principal automatically, so the toggle would be redundant and would
      // let an admin mark a delinquent loan as if nothing were owed.
      principalOnly: false
    });
    setShowManualPaymentModal(true);

    // Fetch initial preview
    if (initialAmount) {
      fetchPaymentPreview(loan.id, initialAmount, shouldApplyLateFee);
    }
  };

  /**
   * Is a scheduled payment owed on this loan right now?
   *
   * Mirrors the server's loanPaymentIsDue(): due today counts as due. The
   * server enforces it regardless -- this only decides whether to show the
   * principal-only option.
   */
  const paymentIsDue = (loan) => {
    if (!loan?.next_payment_date) return false;
    const [y, m, d] = String(loan.next_payment_date).split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return false;
    const due = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= due;
  };

  // ---- Refunds -------------------------------------------------------------

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
  });

  // Step 1: show the loan's payments so the admin picks which one to reverse.
  const openRefundPicker = async (loan) => {
    setRefundLoan(loan);
    setRefundPayments([]);
    setShowRefundPicker(true);
    setLoadingRefundPayments(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/payments`,
        { headers: authHeaders() }
      );
      if (!response.ok) throw new Error('Failed to load payments');
      const data = await response.json();
      setRefundPayments(data.payments || []);
    } catch (err) {
      setError(err.message);
      setShowRefundPicker(false);
    } finally {
      setLoadingRefundPayments(false);
    }
  };

  // Step 2: ask the server what the refund would do, then confirm it.
  const openRefundConfirm = async (payment) => {
    setRefundTarget(payment);
    setRefundPreview(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/payments/${payment.id}/refund-preview`,
        { headers: authHeaders() }
      );
      if (!response.ok) throw new Error('Failed to load refund details');
      setRefundPreview(await response.json());
    } catch (err) {
      setError(err.message);
      setRefundTarget(null);
    }
  };

  const submitRefund = async ({ amount, reason }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/admin/payments/${refundTarget.id}/refund`,
      { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount, reason }) }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.details ? `${data.error}: ${data.details}` : data.error);
    }

    setRefundTarget(null);
    setRefundPreview(null);
    setShowRefundPicker(false);
    setRefundLoan(null);
    // The loan balance, status and due date have all potentially moved.
    loadLoans();
  };

const fetchPaymentPreview = async (loanId, amount, includeLateFee, principalOnly = false) => {
    if (!amount || parseFloat(amount) <= 0) {
      setPaymentPreview(null);
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loanId}/preview-manual-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          includeLateFee,
          principalOnly
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preview');
      }

      const result = await response.json();
      setPaymentPreview(result.breakdown);
    } catch (err) {
      console.error('Preview error:', err);
      setPaymentPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleManualPaymentSubmit = async (e) => {
    e.preventDefault();

    if (!manualPaymentData.amount || parseFloat(manualPaymentData.amount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    // The server refuses anything beyond payoff. Catching it here saves the
    // admin a round trip and says exactly what to enter instead.
    if (paymentPreview?.excessBeyondPayoff > 0) {
      alert(
        `That is $${paymentPreview.excessBeyondPayoff.toFixed(2)} more than this loan needs.\n\n` +
        `Enter $${(paymentPreview.totalAmount - paymentPreview.excessBeyondPayoff).toFixed(2)} ` +
        'to pay it off in full, and return the difference separately.'
      );
      return;
    }

    // A payment below the amount due is recorded, not refused -- the money has
    // already arrived and the books have to say so. But the difference is
    // carried as arrears from here on, so the admin has to say they meant it.
    // The server does not enforce this: the manual path deliberately accepts
    // whatever figure is entered, and this is the point at which a person
    // confirms that figure is right.
    if (paymentPreview?.isPartialPayment && !manualPaymentData.recordAsPartial) {
      alert(
        'This is less than the amount due.\n\n' +
        `$${paymentPreview.remainingDue.total.toFixed(2)} will be carried as arrears ` +
        'and added to the next amount due.\n\n' +
        'Tick "Record as partial payment" to confirm.'
      );
      return;
    }

    const kind = manualPaymentData.principalOnly ? 'principal-only ' : '';
    if (!window.confirm(`Record ${kind}${manualPaymentData.payment_method} payment of $${parseFloat(manualPaymentData.amount).toFixed(2)} for ${manualPaymentLoan.property_title}?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${manualPaymentLoan.id}/record-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(manualPaymentData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to record payment');
      }

      const result = await response.json();
      const fx = result.effect || {};
      const savings = (fx.projectionApplies && fx.extraPrincipal > 0 && fx.paymentsSaved > 0)
        ? `\n\nExtra principal: $${fx.extraPrincipal.toFixed(2)}` +
          `\nSaves ${fx.paymentsSaved} payment${fx.paymentsSaved === 1 ? '' : 's'} ` +
          `and $${fx.interestSaved.toFixed(2)} in interest`
        : '';
      // A notice that did not clear leaves a legal deadline running, so say so
      // rather than letting a "recorded successfully" imply the default is cured.
      const noticeNote = result.noticeCleared
        ? '\n\n⚖️ Cure notice cleared. Default cured.'
        : (result.noticeStillOutstanding
            ? '\n\n⚠️ Short of the cure amount. The cure notice and its deadline REMAIN on this loan.'
            : '');
      alert(
        `Payment recorded successfully!\n\nNew Balance: $${result.newBalance.toFixed(2)}` +
        savings +
        noticeNote +
        (result.paidOff ? '\n\n🎉 LOAN PAID OFF!' : '')
      );
      setShowManualPaymentModal(false);
      setManualPaymentLoan(null);
      setPaymentPreview(null);  // Clear preview
      loadLoans();
    } catch (err) {
      alert(err.message || 'Failed to record payment');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading loans...</p>
      </div>
    );
  }

  if (error) {
    return (
    <div className="admin-loans-error-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Every number on this page — stat cards AND tab counts — comes from this one
  // block, so a card can never disagree with the tab beside it. Each count uses
  // the same predicate as the filter that the matching tab applies.
  // ACTIVE deliberately includes OVERDUE: they overlap, so the two numbers are
  // not meant to sum to the total.
  const activeCount = loans.filter(isActive).length;
  const overdueCount = loans.filter(isOverdue).length;
  const paidOffCount = loans.filter(l => l.status === 'paid_off').length;
  const defaultedCount = loans.filter(l => l.status === 'defaulted').length;
  const archivedCount = loans.filter(l => l.status === 'archived').length;
  // Matches the 'all' filter, which hides archived loans.
  const allCount = loans.filter(l => l.status !== 'archived').length;

  return (
    <div className="admin-loans-container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="admin-page-title">Loan Management</h1>
        <button
          onClick={() => !demoDisabled && navigate('/admin/loans/create')}
          className="btn admin-loans-btn-create"
          style={{ width: '100%', marginBottom: '10px', ...demoStyle }}
          disabled={demoDisabled}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          ✨ Create Custom Loan
        </button>
        <button
          onClick={() => !demoDisabled && navigate('/admin/loans/import')}
          className="btn admin-loans-btn-import"
          style={{ width: '100%', marginBottom: '10px', ...demoStyle }}
          disabled={demoDisabled}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          📥 Import Existing Loan
        </button>
        <button onClick={() => navigate('/admin/loans/defaulted')} className="btn admin-loans-btn-defaulted" style={{ width: '100%', marginBottom: '10px' }}>
          📊 Defaulted Loans Report
        </button>
        <button onClick={() => navigate('/admin/customers-loans-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Customers & Loans
        </button>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-summary admin-loans-summary">
        <div className="summary-card">
          <h3>Active Loans</h3>
          <p className="summary-number">{activeCount}</p>
        </div>
        <div className="summary-card">
          <h3>Overdue</h3>
          <p className="summary-number" style={{ color: 'var(--color-error)' }}>{overdueCount}</p>
        </div>
        <div className="summary-card">
          <h3>Paid Off</h3>
          <p className="summary-number">{paidOffCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-loans-filters">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilter('all')}
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          >
            All ({allCount})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`btn ${filter === 'overdue' ? 'btn-primary' : 'btn-secondary'}`}
            style={filter === 'overdue' ? { backgroundColor: '#dc3545' } : {}}
          >
            Overdue ({overdueCount})
          </button>
          <button
            onClick={() => setFilter('paid_off')}
            className={`btn ${filter === 'paid_off' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Paid Off ({paidOffCount})
          </button>
          <button
            onClick={() => setFilter('defaulted')}
            className={`btn ${filter === 'defaulted' ? 'btn-primary' : 'btn-secondary'}`}
            style={filter === 'defaulted' ? { backgroundColor: '#dc3545' } : {}}
          >
            Defaulted ({defaultedCount})
          </button>
          <button
            onClick={() => setFilter('archived')}
            className={`btn ${filter === 'archived' ? 'btn-primary' : 'btn-secondary'}`}
            style={filter === 'archived' ? { backgroundColor: '#6c757d' } : {}}
          >
            Archived ({archivedCount})
          </button>
        </div>

        <input
          type="text"
          placeholder="Search by customer name, email, or property..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '250px',
            padding: '0.5rem',
            border: '2px solid var(--border-color)',
            borderRadius: '5px',
            fontSize: '16px',
            minHeight: '44px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Desktop Table View */}
      <div className="desktop-only admin-loans-table-container">
        <table className="admin-table admin-loans-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Customer</th>
              <th style={{ textAlign: 'left' }}>Property</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th style={{ textAlign: 'right' }}>Cure Amount</th>
              <th style={{ textAlign: 'right' }}>Monthly</th>
              <th style={{ textAlign: 'center' }}>Due Date</th>
              <th style={{ textAlign: 'center' }}>ROI</th>
              <th style={{ textAlign: 'center' }}>Alerts</th>
            </tr>
          </thead>
          <tbody>
            {filteredLoans.map(loan => {
              const daysOverdue = getDaysOverdue(loan);
              const overdueStatus = isOverdue(loan);
              const tone = overdueTone(loan);
              // Empty string on a loan with no escrow, where the total IS the
              // P&I figure and a split would state the same number twice.
              const monthlySplit = monthlyPaymentSplitText(loan);

              return (
                <tr key={loan.id} style={{ backgroundColor: overdueStatus ? tone.surface : 'white' }}>
                  <td>
                    <div style={{ fontWeight: '600' }}>
                      {loan.first_name} {loan.last_name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{loan.email}</div>
                    {loan.phone && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{formatPhoneNumber(loan.phone)}</div>}
                  </td>
                  <td>
                    <div style={{ fontWeight: '600' }}>{loan.property_title}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{loan.property_location}</div>
                  </td>
                  <td style={{ fontWeight: '600', color: 'var(--color-brand-text)', textAlign: 'right' }}>
                    ${formatCurrency(loan.balance_remaining)}
                  </td>
                  {/* THE CURE AMOUNT, no longer hidden while the loan is
                      "current". A chronically short buyer is never overdue --
                      the due date advances on receipt -- so this column, the
                      one column that would have shown the true figure, printed
                      a dash every month for nine months. It now renders
                      whenever there is something to state: the loan is overdue,
                      or it carries arrears, or both. The dash is reserved for a
                      loan that genuinely owes nothing beyond its next payment. */}
                  <td style={{ textAlign: 'right' }}>
                    {(overdueStatus || arrearsBalance(loan) > 0) ? (
                      <span style={{
                        fontWeight: 'bold',
                        color: overdueStatus ? tone.accent : '#c62828',
                        fontSize: '15px'
                      }}>
                        ${formatCurrency(loan.cure_amount || 0)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    )}
                  </td>
                  {/* The buyer's ACTUAL monthly obligation, with the split
                      beneath it. This column used to show loans.monthly_payment,
                      which is principal and interest alone. */}
                  <td style={{ textAlign: 'right' }}>
                    <div>${formatCurrency(totalMonthlyPayment(loan))}</div>
                    {monthlySplit && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-light)', fontWeight: '400', whiteSpace: 'normal' }}>
                        {monthlySplit}
                      </div>
                    )}
                    {/* How far behind the schedule this buyer is. Sits under the
                        monthly figure because that is the number it modifies:
                        the next payment is this plus the monthly payment. */}
                    {arrearsBalance(loan) > 0 && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-error-text)', fontWeight: '600', whiteSpace: 'normal' }}>
                        Arrears ${formatCurrency(arrearsBalance(loan))}
                      </div>
                    )}
                    {/* What is actually left, as opposed to the term stamped on
                        the loan at origination. loans.term_months is never
                        revised, so on a loan that has paid extra or run behind
                        it describes a schedule that no longer exists. Derived
                        by the server from the balance as it stands, on P&I
                        alone. The stored column is not overwritten. */}
                    {loan.projected_payoff_months !== null && loan.projected_payoff_months !== undefined && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-light)', fontWeight: '400', whiteSpace: 'normal' }}>
                        Projected payoff: {loan.projected_payoff_months} months
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {loan.next_payment_date && loan.status === 'active' ? (
                      <div>
                        <div>{new Date(loan.next_payment_date.split('T')[0].replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                        {overdueStatus && (
                          <div style={{ fontSize: '0.85rem', color: tone.accent, fontWeight: '600' }}>
                            <div>{tone.label}</div>
                            {tone.note && (
                              <div style={{ fontWeight: '500', fontSize: '0.78rem', whiteSpace: 'normal' }}>
                                {tone.note}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {loan.status === 'defaulted' ? (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    ) : loan.property_acquisition_cost ? (
                      <span style={{ fontWeight: '600', color: 'var(--sandy-gold)' }}>
                        {(((loan.property_price - loan.property_acquisition_cost) / loan.property_acquisition_cost) * 100).toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {loan.status === 'active' && (
                      <>
                        {/* GROUP 1: Loan Settings */}
                        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-text-light)', marginBottom: '5px', textTransform: 'uppercase' }}>Loan Settings</div>
                          <div style={{ marginBottom: '5px' }}>
                            <label style={{ fontSize: '11px', color: 'var(--color-text-light)', display: 'block', marginBottom: '3px' }}>Due Day:</label>
                            <select
                              value={loan.payment_due_day || 1}
                              onChange={async (e) => {
                                if (demoDisabled) return;
                                const newDay = parseInt(e.target.value);
                                if (window.confirm(`Change payment due day to the ${formatOrdinalDay(newDay)}?`)) {
                                  try {
                                    const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/payment-due-day`, {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                      },
                                      body: JSON.stringify({ payment_due_day: newDay })
                                    });
                                    if (!response.ok) throw new Error('Failed');
                                    loadLoans();
                                  } catch (err) {
                                    alert('Failed to update payment due day');
                                  }
                                }
                              }}
                              disabled={demoDisabled}
                              style={{ width: '100%', padding: '4px', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box', opacity: demoDisabled ? 0.5 : 1, cursor: demoDisabled ? 'not-allowed' : 'pointer' }}
                              title={demoDisabled ? 'Disabled in demo mode' : ''}
                            >
                              {adminDueDays.map(day => (
                                <option key={day} value={String(day)}>{formatOrdinalDay(day)}</option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={() => {
                              if (demoDisabled) return;
                              setDeedModalLoan(loan);
                              setDeedFormData({
                                deed_type: loan.deed_type || 'Special Warranty Deed',
                                deed_name: loan.deed_name || '',
                                deed_mailing_address: loan.deed_mailing_address || ''
                              });
                              setShowDeedModal(true);
                            }}
                            disabled={demoDisabled}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#6c757d',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            📜 Deed Info {(loan.deed_name || loan.deed_mailing_address) ? '✓' : ''}
                          </button>
                          <button
                            onClick={() => !demoDisabled && toggleAlert(loan.id, loan.alerts_disabled)}
                            disabled={demoDisabled}
                            className="btn btn-small"
                            style={{
                              backgroundColor: loan.alerts_disabled ? '#ffc107' : 'var(--forest-green)',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              ...demoStyle
                            }}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            {loan.alerts_disabled ? '🔕 Alerts Off' : '🔔 Alerts On'}
                          </button>
                        </div>

                        {/* GROUP 2: Contract Section */}
                        <div style={{
                          marginBottom: '10px',
                          padding: '8px',
                          backgroundColor: 'var(--color-surface-sunken)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '4px'
                        }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-text-light)', marginBottom: '5px', textTransform: 'uppercase' }}>Contract</div>
                          {!loan.contract_status && (
                            <button
                              onClick={async () => {
                                if (demoDisabled) return;
                                if (!window.confirm('Generate contract for admin review and signature?')) return;
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/generate-contract`, {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                    }
                                  });
                                  if (!response.ok) throw new Error('Failed');
                                  const data = await response.json();
                                  alert(data.message);
                                  loadLoans();
                                } catch (err) {
                                  alert('Failed to generate contract');
                                }
                              }}
                              disabled={demoDisabled}
                              className="btn btn-small"
                              style={{
                                backgroundColor: '#2c5f2d',
                                color: 'white',
                                width: '100%',
                                fontSize: '11px',
                                ...demoStyle
                              }}
                              title={demoDisabled ? 'Disabled in demo mode' : ''}
                            >
                              📝 Generate
                            </button>
                          )}
                          {/* Contract Status Badge & Inline Actions */}
                          {loan.contract_status === 'pending_admin_signature' && (
                            <>
                              <div style={{ padding: '6px', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center', marginBottom: '5px' }}>
                                ⏳ Pending Admin
                              </div>
                              <div style={{ display: 'flex', gap: '3px', marginBottom: '3px' }}>
                                <button
                                  onClick={async () => {
                                    const contract = await loadContract(loan.id);
                                    if (!contract) { alert('Failed to load contract'); return; }
                                    const modal = document.createElement('div');
                                    modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;';
                                    const container = document.createElement('div');
                                    container.style.cssText = 'background:white;border-radius:10px;max-width:900px;width:100%;max-height:90vh;display:flex;flex-direction:column;';
                                    const header = document.createElement('div');
                                    header.style.cssText = 'padding:20px;border-bottom:2px solid var(--forest-green);';
                                    const h2 = document.createElement('h2');
                                    h2.style.cssText = 'margin:0;color:var(--forest-green);';
                                    h2.textContent = '📄 Contract for Deed - Admin Review';
                                    header.appendChild(h2);
                                    const body = document.createElement('div');
                                    body.style.cssText = 'padding:20px;overflow-y:auto;flex:1;white-space:pre-wrap;font-family:monospace;font-size:12px;line-height:1.6;background:#f5f5f5;';
                                    body.textContent = contract.contract_text;
                                    const footer = document.createElement('div');
                                    footer.style.cssText = 'padding:20px;border-top:2px solid #e0e0e0;display:flex;flex-direction:column;gap:15px;';
                                    const notice = document.createElement('div');
                                    notice.style.cssText = 'background:#fff3cd;padding:15px;border-radius:5px;border:2px solid #ffc107;';
                                    const noticeStrong = document.createElement('strong');
                                    noticeStrong.textContent = '⚠️ Admin Electronic Signature Agreement';
                                    notice.appendChild(noticeStrong);
                                    notice.appendChild(document.createElement('br'));
                                    notice.appendChild(document.createTextNode('By typing your name below, you agree to sign this Contract for Deed electronically as the seller. This signature will be legally binding.'));
                                    const input = document.createElement('input');
                                    input.type = 'text';
                                    input.id = 'adminSignatureInput';
                                    input.placeholder = 'Type your full legal name here';
                                    input.style.cssText = 'padding:12px;border:2px solid var(--forest-green);border-radius:5px;font-size:16px;width:100%;box-sizing:border-box;';
                                    const buttonContainer = document.createElement('div');
                                    buttonContainer.style.cssText = 'display:flex;gap:10px;';
                                    const signBtn = document.createElement('button');
                                    signBtn.id = 'adminSignBtn';
                                    signBtn.style.cssText = 'flex:1;padding:12px;background:var(--forest-green);color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;font-weight:600;';
                                    signBtn.textContent = '✍️ Sign Contract';
                                    const cancelBtn = document.createElement('button');
                                    cancelBtn.id = 'adminCancelBtn';
                                    cancelBtn.style.cssText = 'flex:1;padding:12px;background:#6c757d;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;font-weight:600;';
                                    cancelBtn.textContent = 'Cancel';
                                    buttonContainer.appendChild(signBtn);
                                    buttonContainer.appendChild(cancelBtn);
                                    footer.appendChild(notice);
                                    footer.appendChild(input);
                                    footer.appendChild(buttonContainer);
                                    container.appendChild(header);
                                    container.appendChild(body);
                                    container.appendChild(footer);
                                    modal.appendChild(container);
                                    document.body.appendChild(modal);
                                    cancelBtn.onclick = () => { document.body.removeChild(modal); };
                                    signBtn.onclick = async () => {
                                      const signature = input.value.trim();
                                      if (!signature) { alert('Please enter your full legal name'); return; }
                                      try {
                                        document.body.removeChild(modal);
                                        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/sign-contract`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                                          body: JSON.stringify({ signature })
                                        });
                                        if (!response.ok) throw new Error('Failed');
                                        alert('Contract signed!');
                                        loadLoans();
                                      } catch (err) { alert('Failed to sign contract'); }
                                    };
                                  }}
                                  /* Amber stays amber in both themes, so this ink is fixed rather than
                                     themed: the neutral text token goes near-white in dark mode, which
                                     would put light text on an amber button. */
                                  style={{ flex: 1, padding: '4px', fontSize: '10px', backgroundColor: '#ffc107', color: '#1a1a1a', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                  Sign
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete contract?`)) return;
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/contract`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
                                      });
                                      if (!response.ok) throw new Error('Failed');
                                      alert('Contract deleted');
                                      loadLoans();
                                    } catch (err) { alert('Failed to delete'); }
                                  }}
                                  style={{ padding: '4px 8px', fontSize: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                          {loan.contract_status === 'pending_client_signature' && (
                            <div style={{ padding: '6px', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center' }}>
                              ⏳ Awaiting Customer
                            </div>
                          )}
                          {loan.contract_status === 'fully_executed' && (
                            <>
                              <div style={{ padding: '6px', backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success-text)', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center', marginBottom: '5px' }}>
                                ✅ Executed
                              </div>
                              <div style={{ display: 'flex', gap: '3px', marginBottom: '3px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/download-contract`, {
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
                                      });
                                      if (!response.ok) throw new Error('Failed');
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `Contract_${loan.property_title.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    } catch (err) { alert('Failed to download'); }
                                  }}
                                  style={{ flex: 1, padding: '4px', fontSize: '10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                >
                                  PDF
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete contract?`)) return;
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/contract`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
                                      });
                                      if (!response.ok) throw new Error('Failed');
                                      alert('Contract deleted');
                                      loadLoans();
                                    } catch (err) { alert('Failed to delete'); }
                                  }}
                                  style={{ padding: '4px 8px', fontSize: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* GROUP 3: Actions */}
                        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-text-light)', marginBottom: '5px', textTransform: 'uppercase' }}>Actions</div>
                          <button
                            onClick={() => !demoDisabled && openManualPaymentModal(loan)}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            disabled={demoDisabled}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            💵 Record Payment
                          </button>
                          <button
                            onClick={() => !demoDisabled && openEscrowPaymentModal(loan)}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#0d6efd',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            disabled={demoDisabled}
                            title={demoDisabled ? 'Disabled in demo mode' : 'Record a tax or HOA payment made to the county or association'}
                          >
                            🏛️ Record Tax/HOA Payment
                          </button>
                          <button
                            onClick={() => !demoDisabled && openAdjustEscrowModal(loan)}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            disabled={demoDisabled}
                            title={demoDisabled ? 'Disabled in demo mode' : 'Re-price this loan\'s monthly escrow'}
                          >
                            ⚖️ Adjust Escrow
                          </button>
                          <button
                            onClick={() => !demoDisabled && openRefundPicker(loan)}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#fd7e14',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            disabled={demoDisabled}
                            title={demoDisabled ? 'Disabled in demo mode' : 'Refund a payment on this loan'}
                          >
                            ↩️ Refund Payment
                          </button>
                          <button
                            onClick={() => !demoDisabled && openDefaultModal(loan)}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              marginBottom: '5px',
                              ...demoStyle
                            }}
                            disabled={demoDisabled}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            ⚠️ Mark as Defaulted
                          </button>
                          {daysOverdue >= getNoticeEligibilityDays() && !loan.notice_sent_date && (
                            <button
                              onClick={() => !demoDisabled && openNoticeModal(loan)}
                              className="btn btn-small"
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                width: '100%',
                                fontSize: '11px',
                                marginBottom: '5px',
                                ...demoStyle
                              }}
                              disabled={demoDisabled}
                              title={demoDisabled ? 'Disabled in demo mode' : ''}
                            >
                              📨 Send Notice
                            </button>
                          )}
                          {loan.notice_sent_date && (
                            <div style={{
                              padding: '4px',
                              fontSize: '10px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              borderRadius: '3px',
                              marginBottom: '5px',
                              textAlign: 'center'
                            }}>
                              Notice Sent {new Date(loan.notice_sent_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                          {isPastGracePeriod(loan) && (
                            isLateFeeWaived(loan) ? (
                              <div style={{
                                backgroundColor: 'var(--color-success-bg)',
                                color: 'var(--color-success-text)',
                                width: '100%',
                                fontSize: '11px',
                                padding: '6px',
                                borderRadius: '4px',
                                textAlign: 'center'
                              }}>
                                Late Fee Waived
                              </div>
                            ) : (
                              <button
                                onClick={() => !demoDisabled && waiveLateFee(loan.id)}
                                disabled={demoDisabled}
                                className="btn btn-small"
                                style={{
                                  backgroundColor: '#ffc107',
                                  color: 'white',
                                  width: '100%',
                                  fontSize: '11px',
                                  ...demoStyle
                                }}
                                title={demoDisabled ? 'Disabled in demo mode' : ''}
                              >
                                Waive Late Fee
                              </button>
                            )
                          )}
                        </div>

                        {/* GROUP 4: Danger Zone */}
                        <div style={{
                          marginTop: '10px',
                          padding: '8px',
                          backgroundColor: 'var(--color-error-bg)',
                          border: '1px solid var(--color-error-border)',
                          borderRadius: '4px'
                        }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-error)', marginBottom: '5px', textTransform: 'uppercase' }}>⚠️ Danger Zone</div>
                          <button
                            onClick={async () => {
                              if (demoDisabled) return;
                              if (!window.confirm(`⚠️ DELETE ENTIRE LOAN?\n\nThis will permanently delete:\n• The loan record\n• All payment history\n• All associated data\n\nThe customer account will remain.\n\nThis action CANNOT be undone!`)) return;
                              if (!window.confirm(`Are you absolutely sure? Type the loan ID (${loan.id}) to confirm.`) || prompt('Enter loan ID to confirm:') !== loan.id.toString()) {
                                alert('Deletion cancelled');
                                return;
                              }
                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
                                });
                                if (!response.ok) throw new Error('Failed');
                                alert('Loan deleted successfully');
                                loadLoans();
                              } catch (err) {
                                alert('Failed to delete loan');
                              }
                            }}
                            disabled={demoDisabled}
                            className="btn btn-small"
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              width: '100%',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              ...demoStyle
                            }}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            🗑️ Delete Loan
                          </button>
                        </div>
                      </>
                    )}
                    {loan.status === 'defaulted' && (
                      <>
                        <div style={{ color: 'var(--color-error)', fontWeight: 'bold', fontSize: '12px', marginBottom: '5px' }}>
                          Defaulted {loan.default_date && `on ${new Date(loan.default_date).toLocaleDateString()}`}
                        </div>
                        
                        {/* Archive Button */}
                        <button
                          onClick={async () => {
                            if (demoDisabled) return;
                            if (!window.confirm(`Archive this defaulted loan?\n\nLoan ID: ${loan.id}\nProperty: ${loan.property_title}\n\nThis will:\n• Hide from active dashboard\n• Keep ALL data for reports/tax purposes\n• Allow you to review later\n• You can still delete permanently later if needed`)) return;

                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/archive`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                }
                              });
                              if (!response.ok) throw new Error('Failed');
                              alert('Loan archived successfully');
                              loadLoans();
                            } catch (err) {
                              alert('Failed to archive loan: ' + err.message);
                            }
                          }}
                          disabled={demoDisabled}
                          className="btn btn-small"
                          style={{
                            backgroundColor: '#6c757d',
                            color: 'white',
                            width: '100%',
                            fontSize: '12px',
                            marginTop: '5px',
                            ...demoStyle
                          }}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          📦 Archive Loan
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={async () => {
                            if (demoDisabled) return;
                            if (!window.confirm(`⚠️ PERMANENTLY DELETE DEFAULTED LOAN?\n\nLoan ID: ${loan.id}\nProperty: ${loan.property_title}\nCustomer: ${loan.first_name} ${loan.last_name}\n\nThis will permanently delete:\n• The loan record\n• All payment history ($${loan.total_paid || 0})\n• Default information\n• All associated data\n\nThe property will be set back to 'available'.\n\nThis action CANNOT be undone!`)) return;

                            if (prompt('Type DELETE to confirm:')?.toUpperCase() !== 'DELETE') {
                              alert('Deletion cancelled');
                              return;
                            }

                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}`, {
                                method: 'DELETE',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                }
                              });
                              if (!response.ok) throw new Error('Failed');
                              alert('Defaulted loan deleted successfully. Property set back to available.');
                              loadLoans();
                            } catch (err) {
                              alert('Failed to delete loan: ' + err.message);
                            }
                          }}
                          disabled={demoDisabled}
                          className="btn btn-small"
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            width: '100%',
                            fontSize: '12px',
                            marginTop: '5px',
                            ...demoStyle
                          }}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          🗑️ Permanently Delete
                        </button>
                      </>
                    )}
                    {loan.status === 'archived' && (
                      <>
                        <div style={{ color: 'var(--color-text-light)', fontWeight: 'bold', fontSize: '12px', marginBottom: '5px' }}>
                          Archived {loan.default_date && `(Defaulted ${new Date(loan.default_date).toLocaleDateString()})`}
                        </div>
                        
                        {/* Unarchive Button */}
                        <button
                          onClick={async () => {
                            if (demoDisabled) return;
                            if (!window.confirm('Restore this loan to Defaulted status?')) return;

                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/unarchive`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                }
                              });
                              if (!response.ok) throw new Error('Failed');
                              alert('Loan restored to Defaulted status');
                              loadLoans();
                            } catch (err) {
                              alert('Failed to unarchive loan: ' + err.message);
                            }
                          }}
                          disabled={demoDisabled}
                          className="btn btn-small"
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            width: '100%',
                            fontSize: '12px',
                            marginTop: '5px',
                            ...demoStyle
                          }}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          ↩️ Unarchive
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={async () => {
                            if (demoDisabled) return;
                            if (!window.confirm(`⚠️ PERMANENTLY DELETE ARCHIVED LOAN?\n\nLoan ID: ${loan.id}\nProperty: ${loan.property_title}\n\nThis will remove ALL data from reports and database.\n\nThis action CANNOT be undone!`)) return;

                            if (prompt('Type DELETE to confirm:')?.toUpperCase() !== 'DELETE') {
                              alert('Deletion cancelled');
                              return;
                            }

                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}`, {
                                method: 'DELETE',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                }
                              });
                              if (!response.ok) throw new Error('Failed');
                              alert('Loan permanently deleted');
                              loadLoans();
                            } catch (err) {
                              alert('Failed to delete loan: ' + err.message);
                            }
                          }}
                          disabled={demoDisabled}
                          className="btn btn-small"
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            width: '100%',
                            fontSize: '12px',
                            marginTop: '5px',
                            ...demoStyle
                          }}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          🗑️ Permanently Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="mobile-only">
        {filteredLoans.map(loan => {
          const daysOverdue = getDaysOverdue(loan);
          const overdueStatus = isOverdue(loan);
          const tone = overdueTone(loan);
          return (
            <div
              key={loan.id}
              style={{
                background: overdueStatus ? tone.surface : 'white',
                border: overdueStatus ? `2px solid ${tone.border}` : '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px'
              }}
            >
              {overdueStatus && (
                <div style={{
                  backgroundColor: tone.badgeBg,
                  color: tone.badgeText,
                  padding: '8px',
                  borderRadius: '5px',
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '14px',
                  // Wraps instead of overflowing on a narrow phone.
                  whiteSpace: 'normal',
                  lineHeight: 1.35
                }}>
                  {isWithinGrace(loan) ? '⏳' : '🚨'} {tone.label}
                  {tone.note && (
                    <div style={{ fontWeight: '600', fontSize: '12px', marginTop: '2px' }}>
                      {tone.note}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '10px', borderBottom: '2px solid var(--color-brand-text)', paddingBottom: '10px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: 'var(--color-brand-text)' }}>
                  {loan.first_name} {loan.last_name}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>{loan.email}</div>
                {loan.phone && <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>{formatPhoneNumber(loan.phone)}</div>}
              </div>

              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: '600' }}>{loan.property_title}</div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>{loan.property_location}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Balance</div>
                  <div style={{ fontWeight: '600', color: 'var(--color-brand-text)' }}>
                    ${formatCurrency(loan.balance_remaining)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>
                    {(overdueStatus || arrearsBalance(loan) > 0) ? 'To Cure' : 'Monthly'}
                  </div>
                  <div style={{
                    fontWeight: '600',
                    color: overdueStatus ? tone.accent : (arrearsBalance(loan) > 0 ? '#c62828' : 'inherit')
                  }}>
                    ${formatCurrency(
                      (overdueStatus || arrearsBalance(loan) > 0)
                        ? (loan.cure_amount || 0)
                        : totalMonthlyPayment(loan)
                    )}
                  </div>
                  {/* The split, under the Monthly figure only. The cure amount
                      is a different question -- it carries fees and arrears --
                      and the two are never on screen together in this slot.
                      Same rule as the desktop column: the slot shows what is
                      owed, and only a loan that is current AND square shows a
                      plain monthly figure. */}
                  {!overdueStatus && arrearsBalance(loan) === 0 && monthlyPaymentSplitText(loan) && (
                    <div style={{ fontSize: '11px', color: 'var(--color-text-light)', marginTop: '2px' }}>
                      {monthlyPaymentSplitText(loan)}
                    </div>
                  )}
                </div>
                {loan.next_payment_date && loan.status === 'active' && (
                  <>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Due Date</div>
                      <div style={{ fontWeight: '600' }}>
                        {new Date(loan.next_payment_date.split('T')[0].replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Status</div>
                      <span className={`status-badge status-${loan.status}`}>
                        {loan.status === 'active' ? 'Active' : 'Paid Off'}
                      </span>
                    </div>
                  </>
                )}
                {loan.property_acquisition_cost && loan.status !== 'defaulted' && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>ROI</div>
                    <div style={{ fontWeight: '600', color: 'var(--sandy-gold)' }}>
                      {(((loan.property_price - loan.property_acquisition_cost) / loan.property_acquisition_cost) * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {loan.status === 'active' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Payment Due Day Selector */}
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                      Payment Due Day:
                    </label>
                    <select
                      value={loan.payment_due_day || 1}
                      onChange={async (e) => {
                        if (demoDisabled) return;
                        const newDay = parseInt(e.target.value);
                        if (window.confirm(`Change payment due day to the ${formatOrdinalDay(newDay)}?`)) {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/payment-due-day`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                              },
                              body: JSON.stringify({ payment_due_day: newDay })
                            });
                            if (!response.ok) throw new Error('Failed');
                            loadLoans();
                          } catch (err) {
                            alert('Failed to update payment due day');
                          }
                        }
                      }}
                      disabled={demoDisabled}
                      style={{ width: '100%', padding: '8px', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid var(--color-border)', opacity: demoDisabled ? 0.5 : 1, cursor: demoDisabled ? 'not-allowed' : 'pointer' }}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      {adminDueDays.map(day => (
                        <option key={day} value={String(day)}>{formatOrdinalDay(day)} of Month</option>
                      ))}
                    </select>
                  </div>

                  {/* Toggle Alerts */}
                  <button
                    onClick={() => !demoDisabled && toggleAlert(loan.id, loan.alerts_disabled)}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: loan.alerts_disabled ? '#ffc107' : 'var(--forest-green)',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    {loan.alerts_disabled ? '🔕 Alerts Off - Click to Enable' : '🔔 Alerts On - Click to Disable'}
                  </button>

                  {/* Deed Info Button */}
                  <button
                    onClick={() => {
                      if (demoDisabled) return;
                      setDeedModalLoan(loan);
                      setDeedFormData({
                        deed_type: loan.deed_type || 'Special Warranty Deed',
                        deed_name: loan.deed_name || '',
                        deed_mailing_address: loan.deed_mailing_address || ''
                      });
                      setShowDeedModal(true);
                    }}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    📜 Deed Info {(loan.deed_name || loan.deed_mailing_address) ? '✓' : ''}
                  </button>

                  {/* Send Notice Button (if 30+ days overdue and no notice sent) */}
                  {daysOverdue >= getNoticeEligibilityDays() && !loan.notice_sent_date && (
                    <button
                      onClick={() => !demoDisabled && openNoticeModal(loan)}
                      disabled={demoDisabled}
                      className="btn btn-full-width"
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        padding: '10px',
                        fontSize: '14px',
                        ...demoStyle
                      }}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      📨 Send Default Notice
                    </button>
                  )}

                  {/* Notice Sent Indicator */}
                  {loan.notice_sent_date && (
                    <div style={{
                      padding: '8px',
                      fontSize: '13px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      borderRadius: '5px',
                      textAlign: 'center'
                    }}>
                      📨 Notice Sent {new Date(loan.notice_sent_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  )}

                  {/* Waive Late Fee (if past grace period) */}
                  {isPastGracePeriod(loan) && (
                    isLateFeeWaived(loan) ? (
                      <div style={{
                        backgroundColor: 'var(--color-success-bg)',
                        color: 'var(--color-success-text)',
                        padding: '10px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}>
                        ✓ Late Fee Waived for {new Date(loan.next_payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    ) : (
                      <button
                        onClick={() => !demoDisabled && waiveLateFee(loan.id)}
                        disabled={demoDisabled}
                        className="btn btn-full-width"
                        style={{
                          backgroundColor: '#ffc107',
                          color: 'white',
                          padding: '10px',
                          fontSize: '14px',
                          ...demoStyle
                        }}
                        title={demoDisabled ? 'Disabled in demo mode' : ''}
                      >
                        ⚠️ Waive Late Fee
                      </button>
                    )
                  )}

                  {/* Record Manual Payment */}
                  <button
                    onClick={() => !demoDisabled && openManualPaymentModal(loan)}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    💵 Record Manual Payment
                  </button>

                  {/* Record Tax/HOA Payment: money leaving escrow for the county
                      or the association, the opposite direction to the button
                      above it. */}
                  <button
                    onClick={() => !demoDisabled && openEscrowPaymentModal(loan)}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#0d6efd',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    🏛️ Record Tax/HOA Payment
                  </button>

                  {/* Adjust Escrow */}
                  <button
                    onClick={() => !demoDisabled && openAdjustEscrowModal(loan)}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#6f42c1',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    ⚖️ Adjust Escrow
                  </button>

                  {/* Mark as Defaulted */}
                  <button
                    onClick={() => !demoDisabled && openDefaultModal(loan)}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    ⚠️ Mark as Defaulted
                  </button>

                  {/* Deed Type Selector */}
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--color-text-light)', display: 'block', marginBottom: '4px' }}>
                      Deed Type:
                    </label>
                    <select
                      value={loan.deed_type || 'Special Warranty Deed'}
                      onChange={async (e) => {
                        if (demoDisabled) return;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/deed-type`, {
                            method: 'PATCH',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                            },
                            body: JSON.stringify({ deed_type: e.target.value })
                          });
                          if (!response.ok) throw new Error('Failed');
                          loadLoans();
                        } catch (err) {
                          alert('Failed to update deed type');
                        }
                      }}
                      disabled={demoDisabled}
                      style={{ width: '100%', padding: '8px', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid var(--color-border)', opacity: demoDisabled ? 0.5 : 1, cursor: demoDisabled ? 'not-allowed' : 'pointer' }}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      <option value="Special Warranty Deed">Special Warranty Deed</option>
                      <option value="Quitclaim Deed">Quitclaim Deed</option>
                    </select>
                  </div>

                  {/* Generate Contract */}
                  <button
                    onClick={async () => {
                      if (demoDisabled) return;
                      if (!window.confirm('Generate contract for admin review and signature?')) return;
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/generate-contract`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                          }
                        });
                        if (!response.ok) throw new Error('Failed');
                        const data = await response.json();
                        alert(data.message);
                        loadLoans();
                      } catch (err) {
                        alert('Failed to generate contract');
                      }
                    }}
                    disabled={demoDisabled}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    📄 Generate Contract
                  </button>

                  {/* Contract Status Section */}
                  {loan.contract_status === 'pending_admin_signature' && (
                    <>
                      <div style={{
                        padding: '10px',
                        backgroundColor: 'var(--color-warning-bg)',
                        borderRadius: '5px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-warning-text)',
                        textAlign: 'center'
                      }}>
                        ⏳ Contract: Awaiting Admin Signature
                      </div>
                      <button
                        onClick={async () => {
                          const contract = await loadContract(loan.id);
                          if (!contract) {
                            alert('Failed to load contract');
                            return;
                          }
                          // Review & sign modal code (reuse from desktop)
                          const modal = document.createElement('div');
                          modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;';

                          const container = document.createElement('div');
                          container.style.cssText = 'background:white;border-radius:10px;max-width:900px;width:100%;max-height:90vh;display:flex;flex-direction:column;';

                          const header = document.createElement('div');
                          header.style.cssText = 'padding:20px;border-bottom:2px solid var(--forest-green);';
                          const h2 = document.createElement('h2');
                          h2.style.cssText = 'margin:0;color:var(--forest-green);';
                          h2.textContent = '📄 Contract for Deed - Admin Review';
                          header.appendChild(h2);

                          const body = document.createElement('div');
                          body.style.cssText = 'padding:20px;overflow-y:auto;flex:1;white-space:pre-wrap;font-family:monospace;font-size:12px;line-height:1.6;background:#f5f5f5;';
                          body.textContent = contract.contract_text;

                          const footer = document.createElement('div');
                          footer.style.cssText = 'padding:20px;border-top:2px solid #e0e0e0;display:flex;flex-direction:column;gap:15px;';

                          const notice = document.createElement('div');
                          notice.style.cssText = 'background:#fff3cd;padding:15px;border-radius:5px;border:2px solid #ffc107;';
                          const noticeStrong = document.createElement('strong');
                          noticeStrong.textContent = '⚠️ Admin Electronic Signature Agreement';
                          notice.appendChild(noticeStrong);
                          notice.appendChild(document.createElement('br'));
                          notice.appendChild(document.createTextNode('By typing your name below, you agree to sign this Contract for Deed electronically as the seller. This signature will be legally binding.'));

                          const input = document.createElement('input');
                          input.type = 'text';
                          input.id = 'adminSignatureInput';
                          input.placeholder = 'Type your full legal name here';
                          input.style.cssText = 'padding:12px;border:2px solid var(--forest-green);border-radius:5px;font-size:16px;width:100%;box-sizing:border-box;';

                          const buttonContainer = document.createElement('div');
                          buttonContainer.style.cssText = 'display:flex;gap:10px;';

                          const signBtn = document.createElement('button');
                          signBtn.id = 'adminSignBtn';
                          signBtn.style.cssText = 'flex:1;padding:12px;background:var(--forest-green);color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;font-weight:600;';
                          signBtn.textContent = '✍️ Sign Contract';

                          const cancelBtn = document.createElement('button');
                          cancelBtn.id = 'adminCancelBtn';
                          cancelBtn.style.cssText = 'flex:1;padding:12px;background:#6c757d;color:white;border:none;border-radius:5px;font-size:16px;cursor:pointer;font-weight:600;';
                          cancelBtn.textContent = 'Cancel';

                          buttonContainer.appendChild(signBtn);
                          buttonContainer.appendChild(cancelBtn);

                          footer.appendChild(notice);
                          footer.appendChild(input);
                          footer.appendChild(buttonContainer);

                          container.appendChild(header);
                          container.appendChild(body);
                          container.appendChild(footer);
                          modal.appendChild(container);

                          document.body.appendChild(modal);

                          cancelBtn.onclick = () => {
                            document.body.removeChild(modal);
                          };

                          signBtn.onclick = async () => {
                            const signature = input.value.trim();
                            if (!signature) {
                              alert('Please enter your full legal name');
                              return;
                            }

                            try {
                              document.body.removeChild(modal);
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/sign-contract`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                },
                                body: JSON.stringify({ signature })
                              });
                              if (!response.ok) throw new Error('Failed');
                              alert('Contract signed! Now awaiting customer signature.');
                              loadLoans();
                            } catch (err) {
                              alert('Failed to sign contract');
                            }
                          };
                        }}
                        className="btn btn-full-width"
                        style={{
                          // Fixed ink on a fill that does not follow the theme. See the Sign
                          // button above: amber is a warning colour and stays amber in dark.
                          backgroundColor: '#ffc107',
                          color: '#1a1a1a',
                          padding: '10px',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        📄 Review & Sign Contract
                      </button>
                    </>
                  )}

                  {loan.contract_status === 'pending_client_signature' && (
                    <div style={{
                      padding: '10px',
                      backgroundColor: 'var(--color-warning-bg)',
                      borderRadius: '5px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--color-warning-text)',
                      textAlign: 'center'
                    }}>
                      ⏳ Contract: Awaiting Customer Signature
                    </div>
                  )}

                  {loan.contract_status === 'fully_executed' && (
                    <>
                      <div style={{
                        padding: '10px',
                        backgroundColor: 'var(--color-success-bg)',
                        borderRadius: '5px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-success-text)',
                        textAlign: 'center'
                      }}>
                        ✅ Contract: Fully Executed
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/download-contract`, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                              }
                            });
                            if (!response.ok) throw new Error('Failed');
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Contract_${loan.property_title.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (err) {
                            alert('Failed to download contract PDF');
                          }
                        }}
                        className="btn btn-full-width"
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          padding: '10px',
                          fontSize: '14px'
                        }}
                      >
                        📄 Download PDF
                      </button>
                    </>
                  )}

                  {/* Delete Contract Button (show for any contract status) */}
                  {(loan.contract_status === 'pending_admin_signature' || loan.contract_status === 'pending_client_signature' || loan.contract_status === 'fully_executed') && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete contract for ${loan.property_title}?\n\nThis cannot be undone. Customer will no longer see this contract.`)) return;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}/contract`, {
                            method: 'DELETE',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                            }
                          });
                          if (!response.ok) throw new Error('Failed');
                          alert('Contract deleted successfully');
                          loadLoans();
                        } catch (err) {
                          alert('Failed to delete contract');
                        }
                      }}
                      className="btn btn-full-width"
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        padding: '10px',
                        fontSize: '14px'
                      }}
                    >
                      🗑️ Delete Contract
                    </button>
                  )}

		{/* Delete Loan Button */}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`⚠️ DELETE ENTIRE LOAN?\n\nThis will permanently delete:\n• The loan record\n• All payment history\n• All associated data\n\nThe customer account will remain.\n\nThis action CANNOT be undone!`)) return;
                      
                      if (!window.confirm(`Are you absolutely sure? Type the loan ID (${loan.id}) to confirm.`) || prompt('Enter loan ID to confirm:') !== loan.id.toString()) {
                        alert('Deletion cancelled');
                        return;
                      }
                      
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${loan.id}`, {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                          }
                        });
                        if (!response.ok) throw new Error('Failed');
                        alert('Loan deleted successfully');
                        loadLoans();
                      } catch (err) {
                        alert('Failed to delete loan');
                      }
                    }}
                    className="btn btn-full-width"
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '10px',
                      fontSize: '14px'
                    }}
                  >
                    🗑️ Delete Loan
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredLoans.length === 0 && (
        <div className="empty-state">
          <p>No loans found matching your filters.</p>
        </div>
      )}

      {/* Default Loan Modal */}
      {showDefaultModal && defaultingLoan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '20px', color: 'var(--color-error)' }}>⚠️ Mark Loan as Defaulted</h2>
            
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--color-error-bg)', borderRadius: '5px' }}>
              <strong>Property:</strong> {defaultingLoan.property_title}<br/>
              <strong>Customer:</strong> {defaultingLoan.first_name} {defaultingLoan.last_name}<br/>
              <strong>Total Balance Owed:</strong> ${formatCurrency(defaultingLoan.balance_remaining)}<br/>
              {getDaysOverdue(defaultingLoan) > 0 && (
                <>
                  <strong style={{ color: 'var(--color-error)' }}>Amount to Cure Default:</strong>{' '}
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-error)' }}>
                    ${formatCurrency(defaultingLoan.cure_amount || 0)}
                  </span>
                </>
              )}
            </div>

            <form onSubmit={handleDefaultSubmit}>
              <div className="form-group">
                <label>Default Date *</label>
                <input
                  type="date"
                  value={defaultFormData.default_date}
                  onChange={(e) => setDefaultFormData({...defaultFormData, default_date: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Recovery Costs</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Legal fees, cleanup, repo costs, etc."
                  value={defaultFormData.recovery_costs}
                  onChange={(e) => setDefaultFormData({...defaultFormData, recovery_costs: e.target.value})}
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
                  Legal fees, repo costs, property cleanup, etc.
                </small>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="4"
                  placeholder="Reason for default, contact attempts, etc."
                  value={defaultFormData.default_notes}
                  onChange={(e) => setDefaultFormData({...defaultFormData, default_notes: e.target.value})}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn" style={{ flex: 1, backgroundColor: '#dc3545', color: 'white' }}>
                  Mark as Defaulted
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowDefaultModal(false)} 
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notice Modal */}
      {showNoticeModal && selectedLoan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>📨 Send Default/Cure Notice</h2>
            <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
              <strong>Customer:</strong> {selectedLoan.first_name} {selectedLoan.last_name}<br />
              <strong>Property:</strong> {selectedLoan.property_title}<br />
              <strong>Days Overdue:</strong> {getDaysOverdue(selectedLoan)}
            </p>

            <form onSubmit={handleSendNotice}>
              <div className="form-group">
                <label>Notice Date *</label>
                <input
                  type="date"
                  value={noticeForm.notice_date}
                  onChange={(e) => setNoticeForm({...noticeForm, notice_date: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Postal Method *</label>
                <select
                  value={noticeForm.postal_method}
                  onChange={(e) => setNoticeForm({...noticeForm, postal_method: e.target.value})}
                  required
                >
                  <option value="Certified Mail">Certified Mail</option>
                  <option value="Certified Mail with Return Receipt">Certified Mail with Return Receipt</option>
                  <option value="Priority Mail">Priority Mail</option>
                  <option value="Overnight">Overnight</option>
                </select>
              </div>

              <div className="form-group">
                <label>Postal Cost * (Actual cost)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="10.50"
                  value={noticeForm.postal_cost}
                  onChange={(e) => setNoticeForm({...noticeForm, postal_cost: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Tracking Number *</label>
                <input
                  type="text"
                  placeholder="9400 1000 0000 0000 0000 00"
                  value={noticeForm.tracking_number}
                  onChange={(e) => setNoticeForm({...noticeForm, tracking_number: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="3"
                  placeholder="Customer not responding to calls/emails..."
                  value={noticeForm.notes}
                  onChange={(e) => setNoticeForm({...noticeForm, notes: e.target.value})}
                />
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: 'var(--color-warning-bg)',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                <strong>⚠️ This will trigger the following actions:</strong><br />
                <br />
                <strong>Fees Added to Next Payment:</strong><br />
                - Default/Cure Notice Fee: ${formatCurrency(getNoticeFee())}<br />
                - Postal/Certified Mail: ${noticeForm.postal_cost || '0.00'}<br />
                <br />
                <strong>Customer Deadline:</strong><br />
                {/* Both the date and the sentence below are driven by the cure
                    period frozen on THIS loan, which is what its signed contract
                    states. They were hardcoded to 7 while the configured default
                    is 14, so this preview -- and the wording it says the customer
                    will see -- understated the buyer's statutory cure window by
                    half on a legal default notice. Reading the tenant's current
                    setting instead would be a subtler version of the same bug:
                    correct until an admin edits Business Settings, then silently
                    off for every loan signed before the edit. */}
                - Cure Deadline: {new Date(new Date(noticeForm.notice_date).setDate(new Date(noticeForm.notice_date).getDate() + noticeCurePeriodDays)).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })} ({noticeCurePeriodDays} days from notice date)<br />
                <br />
                <strong style={{ color: 'var(--color-error)' }}>Customer will see:</strong><br />
                <span style={{ fontSize: '13px', fontStyle: 'italic' }}>
                  "You have {noticeCurePeriodDays} days to cure the default. Failure to pay will result in forfeiture of all payments and loss of property rights."
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Record Notice Sent
                </button>
                <button
                  type="button"
                  onClick={() => setShowNoticeModal(false)}
                  className="btn"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Tax/HOA Payment Modal */}
      {showEscrowPaymentModal && escrowPaymentLoan && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)', padding: '30px', borderRadius: '10px',
            maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '10px' }}>🏛️ Record Tax/HOA Payment</h2>
            <p style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: 0 }}>
              Money paid out to the county or the association from escrow collected
              on this loan. This does not change the loan balance.
            </p>
            {/* The county schedule, shown for tax only: HOA has no installment
                columns anywhere in the schema, so there is nothing to show. */}
            {escrowPaymentData.kind === 'tax' && (
              <TaxInstallmentLines schedule={escrowTaxSchedule} />
            )}
            <form onSubmit={submitEscrowPayment}>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={escrowPaymentData.kind}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, kind: e.target.value })}
                  >
                    <option value="tax">Property Tax</option>
                    <option value="hoa">HOA</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount Paid *</label>
                  <input
                    type="number" step="0.01" required placeholder="0.00"
                    value={escrowPaymentData.amount}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, amount: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date Paid *</label>
                  <input
                    type="date" required
                    value={escrowPaymentData.paid_date}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, paid_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tax Year</label>
                  <input
                    type="number"
                    value={escrowPaymentData.tax_year}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, tax_year: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Installment</label>
                  <select
                    value={escrowPaymentData.instalment}
                    onChange={(e) => applyInstalmentSelection(e.target.value)}
                  >
                    <option value="">Whole year</option>
                    <option value="1">1st installment</option>
                    <option value="2">2nd installment</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select
                    value={escrowPaymentData.payment_method}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, payment_method: e.target.value })}
                  >
                    <option value="Check">Check</option>
                    <option value="Wire Transfer">Wire Transfer</option>
                    <option value="ACH">ACH</option>
                    <option value="Cash">Cash</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference</label>
                  <input
                    type="text" placeholder="Check number or confirmation"
                    value={escrowPaymentData.reference}
                    onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, reference: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="2"
                  value={escrowPaymentData.notes}
                  onChange={(e) => setEscrowPaymentData({ ...escrowPaymentData, notes: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Remittance
                </button>
                <button
                  type="button" className="btn" style={{ flex: 1 }}
                  onClick={() => setShowEscrowPaymentModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Escrow Modal */}
      {showAdjustEscrowModal && adjustEscrowLoan && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)', padding: '30px', borderRadius: '10px',
            maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '10px' }}>⚖️ Adjust Escrow</h2>
            <p style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: 0 }}>
              Re-prices the monthly tax and HOA escrow frozen on this loan. Leave a
              field blank to keep the current figure.
            </p>

            <div style={{
              padding: '12px', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)',
              borderRadius: '5px', fontSize: '13px', marginBottom: '15px'
            }}>
              <strong>Written notice is required.</strong> Section 8(b) of the contract
              requires 30 days of written notice before a change takes effect, and
              allows one change every 12 months. No notice is sent by this form. The
              buyer will see the new figure and its effective date on their next
              payment reminder.
            </div>

            {adjustEscrowError && (
              <div style={{
                padding: '12px', backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error-text)',
                borderRadius: '5px', fontSize: '13px', marginBottom: '15px'
              }}>
                {adjustEscrowError}
              </div>
            )}

            <div style={{
              padding: '12px', backgroundColor: 'var(--color-surface-sunken)', borderRadius: '5px',
              fontSize: '13px', marginBottom: '15px'
            }}>
              <strong>Current:</strong> Tax escrow ${formatCurrency(adjustEscrowLoan.monthly_tax_escrow || 0)}
              {' '}per month, HOA escrow ${formatCurrency(adjustEscrowLoan.monthly_hoa_escrow || 0)} per month
            </div>

            <form onSubmit={submitAdjustEscrow}>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>New Annual Tax Amount</label>
                  <input
                    type="number" step="0.01" placeholder="Leave blank to keep"
                    value={adjustEscrowData.annual_tax_amount}
                    onChange={(e) => setAdjustEscrowData({ ...adjustEscrowData, annual_tax_amount: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>New Monthly HOA Fee</label>
                  <input
                    type="number" step="0.01" placeholder="Leave blank to keep"
                    value={adjustEscrowData.monthly_hoa_fee}
                    onChange={(e) => setAdjustEscrowData({ ...adjustEscrowData, monthly_hoa_fee: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Effective Date *</label>
                  <input
                    type="date" required
                    value={adjustEscrowData.effective_date}
                    onChange={(e) => setAdjustEscrowData({ ...adjustEscrowData, effective_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  rows="2" required
                  placeholder="Why this loan is being re-priced"
                  value={adjustEscrowData.reason}
                  onChange={(e) => setAdjustEscrowData({ ...adjustEscrowData, reason: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  type="submit" className="btn btn-primary" style={{ flex: 1 }}
                  disabled={adjustEscrowSaving}
                >
                  {adjustEscrowSaving ? 'Saving...' : 'Apply Change'}
                </button>
                <button
                  type="button" className="btn" style={{ flex: 1 }}
                  onClick={() => setShowAdjustEscrowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

     {/* Manual Payment Modal */}
      {showManualPaymentModal && manualPaymentLoan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>💵 Record Manual Payment</h2>
            
            <div style={{ 
              padding: '15px', 
              backgroundColor: 'var(--color-surface-sunken)', 
              borderRadius: '5px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>Customer:</strong> {manualPaymentLoan.first_name} {manualPaymentLoan.last_name}<br/>
              <strong>Property:</strong> {manualPaymentLoan.property_title}<br/>
              <strong>Current Balance:</strong> ${formatCurrency(manualPaymentLoan.balance_remaining)}<br/>
              {/* The TOTAL owed for a period, then what it is made of. The
                  header used to state loans.monthly_payment under this label,
                  which is principal and interest alone -- so an admin taking a
                  cash payment was told the buyer owed 97.50 when the period
                  owed 107.08. */}
              <strong>Monthly Payment:</strong> ${formatCurrency(totalMonthlyPayment(manualPaymentLoan))}<br/>
              <span style={{ color: 'var(--color-text-light)' }}>
                <strong style={{ fontWeight: '600' }}>Principal &amp; Interest:</strong> ${formatCurrency(manualPaymentLoan.monthly_payment)}
              </span>
              {parseFloat(manualPaymentLoan.monthly_tax_escrow) > 0 && (
                <span style={{ color: 'var(--color-text-light)' }}>
                  <br/><strong style={{ fontWeight: '600' }}>Tax Escrow:</strong> ${formatCurrency(manualPaymentLoan.monthly_tax_escrow)}
                </span>
              )}
              {parseFloat(manualPaymentLoan.monthly_hoa_escrow) > 0 && (
                <span style={{ color: 'var(--color-text-light)' }}>
                  <br/><strong style={{ fontWeight: '600' }}>HOA Escrow:</strong> ${formatCurrency(manualPaymentLoan.monthly_hoa_escrow)}
                </span>
              )}
              {/* What the loan is behind by, and therefore what the amount
                  field prefills to. Shown only when there is something to
                  show: on a loan that is square the amount due IS the monthly
                  payment, and a $0.00 arrears line would be noise on every
                  other loan in the portfolio. */}
              {arrearsBalance(manualPaymentLoan) > 0 && (
                <>
                  <br/>
                  <span style={{ color: 'var(--color-error-text)' }}>
                    <strong style={{ fontWeight: '600' }}>Arrears:</strong> ${formatCurrency(arrearsBalance(manualPaymentLoan))}
                  </span>
                  <br/>
                  <strong>Amount Due:</strong> ${formatCurrency(amountDue(manualPaymentLoan))}
                </>
              )}
            </div>

            <form onSubmit={handleManualPaymentSubmit}>
              <div className="form-group">
                <label>Payment Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={manualPaymentData.amount}
                  onChange={(e) => {
                    setManualPaymentData({...manualPaymentData, amount: e.target.value});
                    fetchPaymentPreview(
                      manualPaymentLoan.id,
                      e.target.value,
                      manualPaymentData.includeLateFee,
                      manualPaymentData.principalOnly
                    );
                  }}
                  required
                  style={{ width: '100%', padding: '10px', fontSize: '16px' }}
                />
              </div>

              {/* Principal-only, offered only when nothing is due. When a
                  payment IS due the excess goes to principal automatically. */}
              {!paymentIsDue(manualPaymentLoan) && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={manualPaymentData.principalOnly}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setManualPaymentData({...manualPaymentData, principalOnly: newValue});
                        fetchPaymentPreview(
                          manualPaymentLoan.id,
                          manualPaymentData.amount,
                          manualPaymentData.includeLateFee,
                          newValue
                        );
                      }}
                      style={{ marginRight: '8px', width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      Principal-only payment
                      <span style={{ display: 'block', fontSize: '12px', color: 'var(--color-text-light)', marginTop: '2px' }}>
                        Nothing is due. The whole amount reduces the balance:
                        no interest, escrow or fees, and the due date does not move.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {/* Late Fee Toggle.

                  Every decision here comes from describeLateFee(), which reads
                  the server's own late_fee_applies / late_fee_waived flags.
                  This block previously keyed off preview.lateFee.isOverdue --
                  a field that ignores grace by design -- and so offered a fee
                  on a loan inside its grace window. Do not reintroduce a local
                  overdue/grace test here. */}
              {lateFeeView.visible && (
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      cursor: lateFeeView.disabled ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      opacity: lateFeeView.disabled ? 0.65 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={manualPaymentData.includeLateFee}
                      disabled={lateFeeView.disabled}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setManualPaymentData({...manualPaymentData, includeLateFee: newValue});
                        fetchPaymentPreview(
                          manualPaymentLoan.id,
                          manualPaymentData.amount,
                          newValue,
                          manualPaymentData.principalOnly
                        );
                      }}
                      style={{ marginRight: '8px', width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      {lateFeeView.label}
                      {lateFeeView.hint && (
                        <span style={{
                          display: 'block',
                          fontSize: '12px',
                          marginTop: '2px',
                          color: lateFeeView.tone === 'due' ? '#dc3545' : '#666'
                        }}>
                          {lateFeeView.hint}
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              )}

              {/* Payment Breakdown Preview */}
              {paymentPreview && (
                <div style={{
                  padding: '20px',
                  backgroundColor: 'var(--color-info-bg)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '2px solid var(--color-info-border)'
                }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: 'var(--color-info-text)' }}>
                    📊 Payment Breakdown
                  </h3>

                  {/* Cure notice banner. The notice went out demanding a
                      specific total; this is that total, and whether the amount
                      entered actually reaches it. A partial payment leaves the
                      notice and its deadline standing. */}
                  {paymentPreview.notice?.outstanding && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '15px',
                      borderRadius: '6px',
                      border: `2px solid ${paymentPreview.notice.willCure ? '#28a745' : '#dc3545'}`,
                      backgroundColor: paymentPreview.notice.willCure ? '#eaf7ee' : '#fdeaea',
                      fontSize: '14px'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px 12px',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        marginBottom: '6px'
                      }}>
                        <span>⚖️ Cure notice outstanding</span>
                        <span>Cure amount: ${paymentPreview.notice.cureAmount.toFixed(2)}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: '1.5' }}>
                        {paymentPreview.notice.cureDeadline && (
                          <div>
                            Cure by {new Date(paymentPreview.notice.cureDeadline).toLocaleDateString()}
                            {paymentPreview.notice.trackingNumber &&
                              ` · tracking ${paymentPreview.notice.trackingNumber}`}
                          </div>
                        )}
                        <div style={{
                          marginTop: '4px',
                          fontWeight: 'bold',
                          color: paymentPreview.notice.willCure ? '#1c7c33' : '#b02a2a'
                        }}>
                          {paymentPreview.notice.willCure
                            ? '✓ This payment cures the default and clears the notice.'
                            : `✗ Short of the cure amount. The notice stays on the loan.` +
                              (paymentPreview.remainingDue
                                ? ` $${paymentPreview.remainingDue.total.toFixed(2)} still required.`
                                : '')}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                    {/* Late Fee */}
                    {paymentPreview.lateFee.expected > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>
                          Late Fee: 
                          {paymentPreview.lateFee.status === 'paid' && ' ✓'}
                          {paymentPreview.lateFee.status === 'partial' && ' ⚠️'}
                          {!paymentPreview.lateFee.wasApplied && ' (waived)'}
                        </span>
                        <strong>${paymentPreview.lateFee.amount.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* Notice Fee. Shown whenever a cure notice is outstanding:
                        the notice demanded this money, so the admin has to see
                        it here before recording a cheque against it. */}
                    {paymentPreview.noticeFee?.expected > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span>
                          Notice Fee:
                          {paymentPreview.noticeFee.status === 'paid' && ' ✓'}
                          {paymentPreview.noticeFee.status === 'partial' && ' ⚠️'}
                        </span>
                        <strong>${paymentPreview.noticeFee.amount.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* Postal Fee */}
                    {paymentPreview.postalFee?.expected > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span>
                          Postage:
                          {paymentPreview.postalFee.status === 'paid' && ' ✓'}
                          {paymentPreview.postalFee.status === 'partial' && ' ⚠️'}
                        </span>
                        <strong>${paymentPreview.postalFee.amount.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* Property Tax */}
                    {paymentPreview.tax.expected > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>
                          Property Tax: 
                          {paymentPreview.tax.status === 'paid' && ' ✓'}
                          {paymentPreview.tax.status === 'partial' && ' ⚠️'}
                        </span>
                        <strong>${paymentPreview.tax.amount.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* HOA Fee */}
                    {paymentPreview.hoa.expected > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>
                          HOA Fee: 
                          {paymentPreview.hoa.status === 'paid' && ' ✓'}
                          {paymentPreview.hoa.status === 'partial' && ' ⚠️'}
                        </span>
                        <strong>${paymentPreview.hoa.amount.toFixed(2)}</strong>
                      </div>
                    )}

                    {/* Loan Payment */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>
                        Loan Payment: 
                        {paymentPreview.loan.status === 'paid' && ' ✓'}
                        {paymentPreview.loan.status === 'partial' && ' ⚠️'}
                        {paymentPreview.loan.status === 'unpaid' && ' ❌'}
                      </span>
                      <strong>${paymentPreview.loan.amount.toFixed(2)}</strong>
                    </div>

                    {/* Principal/Interest breakdown */}
                    {paymentPreview.loan.amount > 0 && (
                      <div style={{ marginLeft: '20px', fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Principal:</span>
                          <span>${paymentPreview.loan.principal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Interest:</span>
                          <span>${paymentPreview.loan.interest.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Extra Principal */}
                    {paymentPreview.extraPrincipal > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: '8px',
                        color: 'var(--color-success)',
                        fontWeight: 'bold'
                      }}>
                        <span>💰 Extra Principal: ✓</span>
                        <span>${paymentPreview.extraPrincipal.toFixed(2)}</span>
                      </div>
                    )}

                    <hr style={{ margin: '12px 0', border: 'none', borderTop: '2px solid var(--color-info-border)' }} />

                    {/* Total */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold' }}>
                      <span>Total Amount:</span>
                      <span>${paymentPreview.totalAmount.toFixed(2)}</span>
                    </div>

                    {/* New Balance */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginTop: '12px',
                      padding: '10px',
                      backgroundColor: paymentPreview.willBePaidOff ? '#d4edda' : '#fff3cd',
                      borderRadius: '5px'
                    }}>
                      <span>New Balance:</span>
                      <strong style={{ color: paymentPreview.willBePaidOff ? '#28a745' : '#856404' }}>
                        ${paymentPreview.newBalance.toFixed(2)}
                        {paymentPreview.willBePaidOff && ' 🎉 PAID OFF!'}
                      </strong>
                    </div>

                    {/* Partial Payment Warning */}
                    {paymentPreview.isPartialPayment && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: 'var(--color-warning-bg)',
                        borderLeft: '4px solid var(--color-warning-border)',
                        borderRadius: '4px'
                      }}>
                        <strong>⚠️ Partial Payment</strong><br/>
                        <span style={{ fontSize: '13px' }}>
                          Partial payment: ${paymentPreview.remainingDue.total.toFixed(2)} will
                          be carried as arrears.
                          {paymentPreview.arrears && (
                            <>
                              {' '}The loan will stand $
                              {paymentPreview.arrears.after.toFixed(2)} behind schedule
                              after this payment, and that figure is added to the next
                              amount due.
                            </>
                          )}
                          <br/>
                          The due date still advances one month.
                        </span>
                        {/* The confirmation. The money has already arrived, so
                            the payment is recorded rather than refused -- but
                            carrying arrears is a decision about this buyer's
                            account, and it is made here, by a person. */}
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          userSelect: 'none',
                          marginTop: '10px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          <input
                            type="checkbox"
                            checked={manualPaymentData.recordAsPartial}
                            onChange={(e) => setManualPaymentData({
                              ...manualPaymentData,
                              recordAsPartial: e.target.checked
                            })}
                            style={{ marginRight: '8px', width: '18px', height: '18px', flexShrink: 0, marginTop: '1px' }}
                          />
                          <span>Record as partial payment</span>
                        </label>
                      </div>
                    )}

                    {/* Overpayment Notice */}
                    {paymentPreview.isOverpayment && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: 'var(--color-info-bg)',
                        borderLeft: '4px solid var(--color-info-border)',
                        borderRadius: '4px'
                      }}>
                        <strong>💰 Extra Principal</strong><br/>
                        <span style={{ fontSize: '13px' }}>
                          Extra ${paymentPreview.extraPrincipal.toFixed(2)} will be applied to principal
                          {paymentPreview.effect?.projectionApplies
                            && paymentPreview.effect.paymentsSaved > 0 && (
                            <>
                              , saving {paymentPreview.effect.paymentsSaved} payment
                              {paymentPreview.effect.paymentsSaved === 1 ? '' : 's'} and
                              ${paymentPreview.effect.interestSaved.toFixed(2)} in interest
                            </>
                          )}
                        </span>
                      </div>
                    )}

                    {/* Beyond payoff. record-payment REFUSES this rather than
                        absorbing it, so warn before the admin submits. */}
                    {paymentPreview.excessBeyondPayoff > 0 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: 'var(--color-error-bg)',
                        borderLeft: '4px solid var(--color-error-border)',
                        borderRadius: '4px'
                      }}>
                        <strong>⛔ More than this loan needs</strong><br/>
                        <span style={{ fontSize: '13px' }}>
                          ${paymentPreview.excessBeyondPayoff.toFixed(2)} beyond payoff.
                          Enter ${(paymentPreview.totalAmount - paymentPreview.excessBeyondPayoff).toFixed(2)} to
                          pay this loan off in full, and return the difference separately.
                        </span>
                      </div>
                    )}

                    {/* Due date effect. Extra principal shortens the term; it
                        never buys a month off. */}
                    {!paymentPreview.willBePaidOff && paymentPreview.effect?.nextPaymentDate && (
                      <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--color-text-light)' }}>
                        Next payment due{' '}
                        {new Date(`${paymentPreview.effect.nextPaymentDate}T12:00:00`)
                          .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {paymentPreview.principalOnly && ' (unchanged: settles no period)'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {loadingPreview && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-light)' }}>
                  <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                  Calculating breakdown...
                </div>
              )}

              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  value={manualPaymentData.payment_date}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, payment_date: e.target.value})}
                  required
                  style={{ width: '100%', padding: '10px' }}
                />
              </div>

              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  value={manualPaymentData.payment_method}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, payment_method: e.target.value})}
                  required
                  style={{ width: '100%', padding: '10px' }}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="zelle">Zelle</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="money_order">Money Order</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Transaction ID / Check Number</label>
                <input
                  type="text"
                  placeholder="Check #1234, Venmo ID, etc."
                  value={manualPaymentData.transaction_id}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, transaction_id: e.target.value})}
                  style={{ width: '100%', padding: '10px' }}
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="3"
                  placeholder="Additional details about this payment..."
                  value={manualPaymentData.notes}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, notes: e.target.value})}
                  style={{ width: '100%', padding: '10px', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={paymentPreview?.isPartialPayment && !manualPaymentData.recordAsPartial}
                  style={{
                    flex: 1,
                    backgroundColor: '#28a745',
                    opacity: (paymentPreview?.isPartialPayment && !manualPaymentData.recordAsPartial) ? 0.65 : 1,
                    cursor: (paymentPreview?.isPartialPayment && !manualPaymentData.recordAsPartial) ? 'not-allowed' : 'pointer'
                  }}
                  title={
                    (paymentPreview?.isPartialPayment && !manualPaymentData.recordAsPartial)
                      ? 'Tick "Record as partial payment" to record less than the amount due'
                      : ''
                  }
                >
                  Record Payment
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowManualPaymentModal(false);
                    setManualPaymentLoan(null);
                    setPaymentPreview(null);
                  }}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deed Info Modal */}
      {showDeedModal && deedModalLoan && (
        <div
          onClick={() => setShowDeedModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>📜 Deed Information</h2>
              <button
                onClick={() => setShowDeedModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: 'var(--color-text-light)',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
              <div style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: 'var(--color-success-bg)',
                borderRadius: '5px',
                borderLeft: '4px solid var(--color-brand-text)'
              }}>
                <div style={{ fontWeight: '600', color: 'var(--color-brand-text)' }}>
                  {deedModalLoan.first_name} {deedModalLoan.last_name}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>
                  {deedModalLoan.property_title}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Deed Type
                </label>
                <select
                  value={deedFormData.deed_type}
                  onChange={(e) => setDeedFormData({...deedFormData, deed_type: e.target.value})}
                >
                  <option value="Special Warranty Deed">Special Warranty Deed</option>
                  <option value="Quitclaim Deed">Quitclaim Deed</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Deed Name
                </label>
                <input
                  type="text"
                  value={deedFormData.deed_name}
                  onChange={(e) => setDeedFormData({...deedFormData, deed_name: e.target.value})}
                  placeholder="Name(s) the deed will be made out to"
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
                  e.g., "John and Jane Smith" or "Smith Family Trust"
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Deed Mailing Address
                </label>
                <textarea
                  value={deedFormData.deed_mailing_address}
                  onChange={(e) => setDeedFormData({...deedFormData, deed_mailing_address: e.target.value})}
                  placeholder="Full mailing address where deed will be sent"
                  rows="3"
                  style={{ resize: 'vertical' }}
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
                  Address where the deed will be mailed when loan is paid off
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={async () => {
                    if (demoDisabled) return;
                    try {
                      // Update deed type
                      await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${deedModalLoan.id}/deed-type`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                        },
                        body: JSON.stringify({ deed_type: deedFormData.deed_type })
                      });

                      // Update deed info (name and address)
                      await fetch(`${process.env.REACT_APP_API_URL}/admin/loans/${deedModalLoan.id}/deed-info`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                        },
                        body: JSON.stringify({
                          deed_name: deedFormData.deed_name,
                          deed_mailing_address: deedFormData.deed_mailing_address
                        })
                      });

                      setShowDeedModal(false);
                      loadLoans();
                    } catch (err) {
                      console.error('Failed to save deed info:', err);
                      alert('Failed to save deed information');
                    }
                  }}
                  disabled={demoDisabled}
                  className="btn btn-primary"
                  style={{ flex: 1, ...demoStyle }}
                  title={demoDisabled ? 'Disabled in demo mode' : ''}
                >
                  💾 Save
                </button>
                <button
                  onClick={() => setShowDeedModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Refund step 1: which payment? */}
      {showRefundPicker && refundLoan && (
        <div
          onClick={() => { setShowRefundPicker(false); setRefundLoan(null); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 999,
            overflowY: 'auto'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: '8px',
              padding: '20px',
              width: '100%',
              maxWidth: '540px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxSizing: 'border-box'
            }}
          >
            <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>Refund a Payment</h3>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '14px' }}>
              {refundLoan.property_title || `Loan #${refundLoan.id}`}, current balance{' '}
              {formatCurrency(refundLoan.balance_remaining)}
            </div>

            {loadingRefundPayments && (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--color-text-light)' }}>
                Loading payments…
              </div>
            )}

            {!loadingRefundPayments && refundPayments.length === 0 && (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--color-text-light)' }}>
                No payments recorded on this loan.
              </div>
            )}

            {!loadingRefundPayments && refundPayments.map(payment => (
              <div
                key={payment.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px',
                  marginBottom: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  backgroundColor: payment.isRefund ? '#fff5f5' : '#fff'
                }}
              >
                <div style={{ flex: '1 1 220px', fontSize: '0.85rem' }}>
                  <div style={{ fontWeight: 600 }}>
                    {payment.isRefund ? '↩ Refund ' : ''}
                    {formatCurrency(Math.abs(payment.amount))}
                    <span style={{ fontWeight: 400, color: 'var(--color-text-light)' }}>
                      {' '}· {new Date(payment.paymentDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ color: 'var(--color-text-light)', textTransform: 'capitalize' }}>
                    {(payment.paymentType || '').replace('_', ' ')} ·{' '}
                    {payment.isSquarePayment ? 'Square' : (payment.paymentMethod || 'manual')}
                  </div>
                  {payment.refundedAmount > 0 && !payment.isRefund && (
                    <div style={{ color: 'var(--color-error)' }}>
                      {formatCurrency(payment.refundedAmount)} already refunded
                    </div>
                  )}
                  {payment.isRefund && payment.refundReason && (
                    <div style={{ color: 'var(--color-text-light)' }}>{payment.refundReason}</div>
                  )}
                </div>

                {payment.refundable && (
                  <button
                    onClick={() => openRefundConfirm(payment)}
                    className="btn btn-small"
                    style={{
                      backgroundColor: '#fd7e14',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Refund
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={() => { setShowRefundPicker(false); setRefundLoan(null); }}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '10px', padding: '12px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Refund step 2: confirm exactly what will happen. */}
      {refundTarget && refundPreview && (
        <RefundModal
          isOpen
          onClose={() => { setRefundTarget(null); setRefundPreview(null); }}
          onSubmit={submitRefund}
          title="Confirm Refund"
          maxRefundable={refundPreview.maxRefundable}
          alreadyRefunded={refundPreview.alreadyRefunded}
          methodLabel={
            refundPreview.isSquarePayment
              ? 'Refunds the card through Square, and reverses the loan here.'
              : 'Recorded outside Square. This reverses the loan only. Return the money the same way it came in.'
          }
          lines={[
            { label: 'Customer', value: refundPreview.payment.customerName || '—' },
            { label: 'Property', value: refundPreview.payment.propertyTitle || '—' },
            { label: 'Paid', value: new Date(refundPreview.payment.paymentDate).toLocaleDateString() },
            { label: 'Loan portion', value: formatCurrency(refundPreview.payment.breakdown.loanPayment), muted: true },
            { label: 'Tax (escrow)', value: formatCurrency(refundPreview.payment.breakdown.tax), muted: true },
            { label: 'HOA (escrow)', value: formatCurrency(refundPreview.payment.breakdown.hoa), muted: true },
            { label: 'Late fee', value: formatCurrency(refundPreview.payment.breakdown.lateFee), muted: true },
            { label: 'Payment total', value: formatCurrency(refundPreview.payment.amount), strong: true }
          ]}
          renderEffect={(amount) => {
            // The server returns the effect of a FULL refund. Scale it for a
            // partial so the figure shown is the one that will actually apply.
            const share = refundPreview.maxRefundable > 0
              ? amount / refundPreview.maxRefundable
              : 0;
            const restored = refundPreview.ifFullyRefunded.balanceRestored * share;
            const newBalance = refundPreview.loan.currentBalance + restored;
            const isFull = Math.abs(amount - refundPreview.maxRefundable) < 0.005;

            return (
              <>
                <div>
                  Balance goes from <strong>{formatCurrency(refundPreview.loan.currentBalance)}</strong>
                  {' '}up to <strong>{formatCurrency(newBalance)}</strong>.
                </div>
                {refundPreview.ifFullyRefunded.clampedToLoanAmount && isFull && (
                  <div style={{ marginTop: '4px', color: 'var(--color-warning-text)' }}>
                    Capped at the original loan amount of{' '}
                    {formatCurrency(refundPreview.loan.originalLoanAmount)}.
                  </div>
                )}
                {isFull && refundPreview.ifFullyRefunded.nextPaymentDate !== refundPreview.loan.nextPaymentDate && (
                  <div style={{ marginTop: '4px' }}>
                    Next payment moves back to{' '}
                    <strong>
                      {new Date(refundPreview.ifFullyRefunded.nextPaymentDate).toLocaleDateString()}
                    </strong>.
                  </div>
                )}
                {isFull && refundPreview.ifFullyRefunded.newStatus !== refundPreview.loan.status && (
                  <div style={{ marginTop: '4px' }}>
                    Loan status returns to <strong>{refundPreview.ifFullyRefunded.newStatus}</strong>.
                  </div>
                )}
                {!isFull && (
                  <div style={{ marginTop: '4px', color: 'var(--color-text-light)' }}>
                    Partial refund: the due date stays where it is, since the
                    payment still counts as made.
                  </div>
                )}
              </>
            );
          }}
          warning={
            <>
              {refundPreview.isSquarePayment
                ? 'This sends a real refund to the customer’s card through Square.'
                : 'No card is touched. Send the money back the way it arrived (Venmo, cash, check).'}
              {(refundPreview.payment.breakdown.tax > 0 || refundPreview.payment.breakdown.hoa > 0) && (
                <div style={{ marginTop: '6px' }}>
                  This payment included tax/HOA escrow. A refund returns that
                  escrow too. If you have already forwarded it to the county or
                  the HOA, you will be out of pocket until you reclaim it.
                </div>
              )}
            </>
          }
        />
      )}

    </div>
  );
}

export default AdminLoans;